import React, { useEffect, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";

import classNames from "classnames";

import LeagueSummary from "./LeagueSummary";

import { alertActions } from "../../features/alert/alertSlice";
import { selectSetting } from "../../features/global/globalSlice";
import { leagueManagementActions } from "../../features/leaguemgmt/leagueManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { getSummary } from "../../lib/api";
import { LEAGUE_LOGO_URL } from "../../lib/globals";
import { ILeague, ILeagueSummary } from "../../types/api";
import { AsyncStatuses } from "../../types/shared";

interface Filters {
    ageLevels: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    designation: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    seasons: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    year: {
        options: Array<number>;
        filter: number | undefined;
    };
}

const LeaguesTable = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const { leagues, years } = useAppSelector(state => state.leaguemanagement);
    const currentYear = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    );
    const [summary, setSummary] = useState<ILeagueSummary>();
    const [summaryFetchStatus, setSummaryFetchStatus] =
        useState<AsyncStatuses>(undefined);

    const [showSummary, setShowSummary] = useState<Record<number, boolean>>(
        leagues.reduce(
            (acc, l) => {
                acc[l.id] = false;
                return acc;
            },
            {} as Record<number, boolean>,
        ),
    );

    useEffect(() => {
        setShowSummary(
            leagues.reduce(
                (acc, l) => {
                    acc[l.id] = false;
                    return acc;
                },
                {} as Record<number, boolean>,
            ),
        );
    }, [leagues]);

    const [filters, setFilters] = useState<Filters>({
        ageLevels: {
            options: Array.from(new Set(leagues.map(l => l.ageLevel))),
            filter: undefined,
        },
        designation: {
            options: Array.from(
                new Set(
                    leagues
                        .filter(l => l.designation !== null)
                        .map(l => l.designation),
                ),
            ) as string[],
            filter: undefined,
        },
        seasons: {
            options: Array.from(
                new Set(
                    leagues.filter(l => l.season !== null).map(l => l.season),
                ),
            ) as string[],
            filter: undefined,
        },
        year: {
            options: years
                ? Array.from(Object.keys(years)).map(y => parseInt(y))
                : [],
            filter: parseInt(currentYear?.setting) || undefined,
        },
    });

    const handleSummaryClick = async (league: ILeague) => {
        setShowSummary(prev => {
            const isOpen = prev[league.id];

            return Object.keys(prev).reduce(
                (acc, key) => {
                    acc[Number(key)] =
                        Number(key) === league.id ? !isOpen : false;
                    return acc;
                },
                {} as Record<number, boolean>,
            );
        });

        if (showSummary[league.id] === false) {
            try {
                setSummaryFetchStatus("pending");
                const response = await getSummary(league.id);
                setSummaryFetchStatus("success");

                setSummary(response.summary);
            } catch (error: any) {
                setSummaryFetchStatus("failed");
                setShowSummary(prev => ({
                    ...prev,
                    [league.id]: false,
                }));

                dispatch(
                    alertActions.error({
                        level: "danger",
                        title: `Loading summary for league ${league.ageLevel}${league.designation ? (league.direction ? ` ${league.designation} ${league.direction}` : ` ${league.designation}`) : ""} failed with the following error:`,
                        text:
                            error +
                            "\n\nIf the problem persists, please contact the site admin.",
                        dismissible: true,
                    }),
                );
            }
        }
    };

    return (
        <>
            <Row className="leagueFilters">
                <Col sm={6} lg={4} xl={3}>
                    <h4>Age Level:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.ageLevels.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                ageLevels: {
                                    ...prev.ageLevels,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col sm={6} lg={4} xl={3} className="pt-2 pt-sm-0">
                    <h4>League Designation:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.designation.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                designation: {
                                    ...prev.designation,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col sm={6} lg={4} xl={3} className="pt-2 pt-sm-0">
                    <h4>League Season:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.seasons.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                seasons: {
                                    ...prev.seasons,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col sm={6} lg={4} xl={3} className="pt-2 pt-sm-0">
                    <h4>League Year:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={false}
                        options={filters.year.options.map(y => {
                            return {
                                value: y,
                                label: y,
                            };
                        })}
                        defaultValue={
                            filters.year.filter
                                ? {
                                      value: filters.year.filter,
                                      label: filters.year.filter,
                                  }
                                : undefined
                        }
                        onChange={y => {
                            y?.value &&
                                dispatch(
                                    leagueManagementActions.getLeagues(y.value),
                                );
                            setFilters(prev => ({
                                ...prev,
                                year: {
                                    ...prev.year,
                                    filter: y?.value,
                                },
                            }));
                        }}
                    />
                </Col>
            </Row>
            <Table className="leaguesTable" striped bordered>
                <thead>
                    <tr>
                        <th>League Name</th>
                        <th>Season</th>
                        <th>Status</th>
                        <th>League Summary</th>
                        <th>Edit League</th>
                    </tr>
                </thead>
                <tbody>
                    {leagues
                        .filter(l =>
                            filters.ageLevels.filter
                                ? filters.ageLevels.filter.includes(l.ageLevel)
                                : true,
                        )
                        .filter(l =>
                            filters.designation.filter
                                ? l.designation
                                    ? filters.designation.filter.includes(
                                          l.designation,
                                      )
                                    : false
                                : true,
                        )
                        .filter(l =>
                            filters.seasons.filter
                                ? filters.seasons.filter.includes(l.season)
                                : true,
                        )
                        .filter(y =>
                            filters.year.filter
                                ? y.year === filters.year.filter
                                : true,
                        )
                        .map(l => (
                            <React.Fragment key={l.id}>
                                <tr>
                                    <td
                                        className={classNames({
                                            "bg-primary-subtle":
                                                showSummary[l.id],
                                        })}
                                    >
                                        {l.logo && (
                                            <img
                                                src={`${LEAGUE_LOGO_URL}${l.logo}`}
                                                alt={`${l.ageLevel}${
                                                    l.designation
                                                        ? ` ${l.designation}`
                                                        : ""
                                                } logo`}
                                                className="leagueLogo"
                                            />
                                        )}{" "}
                                        {`${l.ageLevel} ${
                                            l.designation ? l.designation : ""
                                        } ${l.direction ? l.direction : ""}`}
                                    </td>
                                    <td
                                        className={classNames({
                                            "bg-primary-subtle":
                                                showSummary[l.id],
                                        })}
                                    >
                                        {l.season}
                                    </td>
                                    <td
                                        className={classNames({
                                            "bg-primary-subtle":
                                                showSummary[l.id],
                                        })}
                                    >
                                        {parseInt(l.active.toString()) === 1
                                            ? "Active"
                                            : "Inactive"}
                                    </td>
                                    <td
                                        className={classNames({
                                            "bg-primary-subtle":
                                                showSummary[l.id],
                                        })}
                                    >
                                        <span
                                            onClick={() =>
                                                handleSummaryClick(l)
                                            }
                                            className={classNames("link")}
                                        >
                                            League summary
                                        </span>
                                    </td>
                                    <td
                                        className={classNames({
                                            "bg-primary-subtle":
                                                showSummary[l.id],
                                        })}
                                    >
                                        <Link
                                            to={`/league-management/league/${l.id}/edit`}
                                        >
                                            edit
                                        </Link>
                                    </td>
                                </tr>
                                {showSummary[l.id] && (
                                    <tr
                                        className={classNames({
                                            collapse: true,
                                            show: showSummary[l.id],
                                        })}
                                        id={`summary-${l.id}`}
                                    >
                                        <td
                                            colSpan={5}
                                            className={classNames(
                                                "bg-primary-subtle",
                                            )}
                                        >
                                            <LeagueSummary
                                                summary={summary}
                                                fetchStatus={summaryFetchStatus}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                </tbody>
            </Table>
        </>
    );
};

export default LeaguesTable;
